import React, { useEffect } from "react";
import { bool, func, shape, number, oneOfType, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { Redirect } from "react-router-dom";

import { CorporateCardAccountStatus, Box, Text } from "@ui-kit/components/index.es";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as layoutActions } from "reducers/layout";
import Notification from "pages/_components/Notification";
import Head from "pages/_components/Head";
import { resizableRoute } from "pages/_components/Resizable";
import * as i18n from "util/i18n";
import * as config from "util/config";
import { actions, selectors } from "reducers/corporateCard";
import { ENVIRONMENT_TYPE } from "constants.js";
import { isUxDashboardImprovementsEnabled, dispatchV3ShellLayout } from "util/v3ShellLayoutSync";

function AccountStatementsPage(props) {
    AccountStatementsPage.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        activeEnvironment: shape({}).isRequired,
        statements: shape({}).isRequired,
        fullStatementsList: shape({}),
        isFetching: bool.isRequired,
        pagination: shape({}).isRequired,
        localPage: number,
        downloadingFileName: oneOfType([string, number]),
        selectedYear: oneOfType([string, number]),
        embeddedInV3Shell: bool,
    };

    AccountStatementsPage.defaultProps = {
        fullStatementsList: [],
        localPage: 1,
        downloadingFileName: null,
        selectedYear: null,
        embeddedInV3Shell: false,
    };

    const {
        dispatch,
        isDesktop,
        activeEnvironment,
        statements,
        fullStatementsList,
        isFetching,
        pagination,
        localPage,
        downloadingFileName,
        selectedYear,
        embeddedInV3Shell,
    } = props;

    useEffect(() => {
        dispatch(actions.resetState());
    }, [dispatch]);

    useEffect(() => {
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatchV3ShellLayout(dispatch, {
            titleKey: "corporateCard.accountStatements.title",
            goBackPath: "/v3/dashboard",
            rightContentMobile: null,
            scopes: ["corporateCard"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, embeddedInV3Shell]);

    const showCorporateCardSummary = config.getBoolean("frontend.show.corporateCard.summary", false);
    const hasPermission = activeEnvironment?.permissions["corporateCard.summary"];
    const isRetailEnvironment = activeEnvironment?.type === ENVIRONMENT_TYPE.RETAIL;

    if (!showCorporateCardSummary || !hasPermission || isRetailEnvironment) {
        return <Redirect to="/desktop" />;
    }

    const businessName = activeEnvironment?.businessName || activeEnvironment?.name || "";

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push("/v3/dashboard"));
            return;
        }
        dispatch(push("/desktop"));
    };

    const yearsOptions = () => {
        const currentYear = new Date().getFullYear();
        const maxBackYear = config.getInteger("frontend.show.corporateCard.summar.maxBackYear", 3);
        const years = [];

        years.push({ value: currentYear, label: currentYear.toString() });

        for (let i = 1; i <= maxBackYear; i++) {
            years.push({ value: currentYear - i, label: (currentYear - i).toString() });
        }

        return years;
    };

    const handleFilterSubmit = (year) => {
        dispatch(actions.setSelectedYear(year?.value));
        dispatch(actions.listStatementsRequest(year?.value));
    };

    const handleDownload = (id) => {
        dispatch(actions.downloadStatementRequest(id));
    };

    const getPaginatedStatements = () => {
        if (!fullStatementsList || !Array.isArray(fullStatementsList) || fullStatementsList.length === 0) {
            return null;
        }

        const rowsPerPage = pagination.itemsPerPage || 8;
        const currentLocalPage = localPage || 1;

        if (fullStatementsList.length === 0) {
            return { paginatedStatements: [], totalPages: 0, currentPage: currentLocalPage };
        }

        const totalPages = Math.ceil(fullStatementsList.length / rowsPerPage);
        const validPage = Math.max(1, Math.min(currentLocalPage, totalPages));
        const startIndex = (validPage - 1) * rowsPerPage;

        if (isDesktop) {
            const paginatedStatements = fullStatementsList.slice(startIndex, startIndex + rowsPerPage);
            return { paginatedStatements, totalPages, currentPage: validPage };
        }

        const paginatedStatements = fullStatementsList.slice(0, startIndex + rowsPerPage);
        return { paginatedStatements, totalPages, currentPage: validPage };
    };

    const formattedStatements = () => {
        const paginatedData = getPaginatedStatements();
        const statementsToFormat = paginatedData ? paginatedData.paginatedStatements : statements || [];

        if (!statementsToFormat || !Array.isArray(statementsToFormat)) {
            return [];
        }

        return statementsToFormat.map((statement) => ({
            id: statement.fileName,
            label: statement.displayName || statement.cutDate || statement.fileName,
        }));
    };

    const paginationProps = () => {
        const paginatedData = getPaginatedStatements();
        const currentPage = paginatedData ? paginatedData.currentPage : localPage || 1;
        const totalPages = paginatedData ? paginatedData.totalPages : pagination.totalPages || 0;

        const handlePageChange = (page) => {
            dispatch(actions.setLocalPage(page));
        };

        return {
            action: handlePageChange,
            pageNumber: currentPage,
            totalPages,
        };
    };

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="corporateCard" />}
            {(isDesktop || !embeddedInV3Shell) && (
                <Box className="admin-detail-head px-0" mx={isDesktop ? 48 : null}>
                    {isDesktop ? (
                        <Box>
                            <Head onBack={handleBack} accessibilityTextId="corporateCard.accountStatements.title" />
                            <Text variant="h2" className="font-light font-weight-300">
                                {i18n.get("corporateCard.accountStatements.title")}
                            </Text>
                        </Box>
                    ) : (
                        <Head
                            onBack={handleBack}
                            title="corporateCard.accountStatements.title"
                            titleClassName="header-mobile-title-background-blue"
                            headerClassName="blue-main-header-mobile"
                            hideNavbarInMobile
                        />
                    )}
                </Box>
            )}
            <Box className={isDesktop ? "p-5" : "p-3"}>
                <CorporateCardAccountStatus
                    corporateCardName={businessName}
                    yearsOptions={yearsOptions()}
                    defaultYear={yearsOptions()[0]}
                    filterSubmit={handleFilterSubmit}
                    handleDownload={handleDownload}
                    paginationProps={paginationProps()}
                    loading={isFetching}
                    statements={formattedStatements()}
                    listLoading={isFetching}
                    listEmptyStateText={i18n.get("corporateCard.accountStatements.list.empty")}
                    downloadingId={downloadingFileName}
                    showEmptyState={selectedYear !== null}
                    yearPlaceholder={i18n.get("corporateCard.accountStatements.year.placeholder")}
                    selectedYear={selectedYear}
                    showNewCardDesign
                />
            </Box>
        </>
    );
}

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    statements: selectors.getStatements(state),
    fullStatementsList: selectors.getFullStatementsList(state),
    isFetching: selectors.getIsFetching(state),
    pagination: selectors.getPagination(state),
    localPage: selectors.getLocalPage(state),
    downloadingFileName: selectors.getDownloadingFileName(state),
    selectedYear: selectors.getSelectedYear(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountStatementsPage);
